import { useState, useRef, useEffect } from "react";
import { getCategoryById } from "./PostCategoriesConfig";
import { getMyAgenciesMappedIDs } from "../../Utils/Utilities";

// ─── Skeleton Primitive ──────────────────────────────────────────────────────
export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse bg-sahi-disable rounded-md ${className}`} />;
}

// ─── Avatar Component ────────────────────────────────────────────────────────
const AVATAR_BG_COLORS = [
  "bg-sahi-green",
  "bg-sahi-dark-blue",
  "bg-sahi-orange",
  "bg-sahi-yellow"
];

function getAvatarBgColor(str = "") {
  if (!str) return AVATAR_BG_COLORS[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % AVATAR_BG_COLORS.length;
  return AVATAR_BG_COLORS[index];
}

export function Avatar({ src, uName, aName, name, size = 42, color, className = "" }) {
  const [imgErr, setImgErr] = useState(false);
  const displayName = uName || aName || name || "";

  const initials = displayName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const bgColorClass = color || getAvatarBgColor(displayName);

  if (src && !imgErr) {
    return (
      <img
        src={src}
        alt={displayName}
        onError={() => setImgErr(true)}
        style={{ width: size, height: size }}
        className={`rounded-lg object-cover bg-sahi-disable border border-sahi-border-color ${className}`}
      />
    );
  }

  return (
    <div
      style={{ width: size, height: size, fontSize: size * 0.36 }}
      className={`rounded-lg flex items-center justify-center text-white font-bold shrink-0 ${bgColorClass} ${className}`}
    >
      {initials}
    </div>
  );
}


// ─── Time Ago Helper ─────────────────────────────────────────────────────────
export function timeAgo(dt) {
  if (!dt) return "";
  const now = new Date();
  const then = new Date(dt);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}w`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo`;
  return `${Math.floor(diff / 31536000)}y`;
}

// ─── Category Badge Component ────────────────────────────────────────────────
export function CategoryBadge({ type, className = "" }) {
  const meta = getCategoryById(type);
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 sm:px-2.5 rounded-[5px] sm:rounded-[6px] text-[10.5px] sm:text-xs font-semibold leading-tight ${meta.badgeColor} ${className}`}
    >
      <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" strokeWidth={2.2} />
      <span>{meta.label}</span>
    </span>
  );
}

// ─── Liked-by-User Helper ────────────────────────────────────────────────────
export function isLikedByUser(post, user) {
  const uid = String(user?.UserID || "");
  if (!uid || !post?.LikedUIDs) return false;
  return post.LikedUIDs.split(",").map((s) => s.trim()).includes(uid);
}

// ─── Post Ownership Helper ────────────────────────────────────────────────────
// The same account can hold a different UserID/AgencyID under each of its MyAgencies profiles -
// a post created "as" another of my own agencies is still mine (e.g. for delete), not just posts
// created under whichever agency happens to be active right now. Checked everywhere post
// ownership matters: PostCard's report/delete menu, PostDetails' delete button.
export function isPostOwnedByCurrentUser(post, currentUser) {
  if (!post) return false;

  const postUserID = Number(post?.CreatedUserID || 0);
  const currentUserID = Number(currentUser?.UserID || 0);

  if (!postUserID) return false;

  if (currentUserID > 0 && postUserID === currentUserID) {
    return true;
  }

  const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);
  return myUserIDs.includes(postUserID);
}

// ─── Post Media Grid (1 to 10 photos) ─────────────────────────────────────────
export function PostMediaGrid({ photos = [], title = "", onImageClick = null, className = "" }) {
  const [failedImages, setFailedImages] = useState({});

  const validPhotos = (photos || []).filter((_, idx) => !failedImages[idx]);
  if (!validPhotos.length) return null;

  const count = validPhotos.length;

  const handleImgError = (idx) => {
    setFailedImages((prev) => ({ ...prev, [idx]: true }));
  };

  if (count === 1) {
    return (
      <div
        className={`w-full overflow-hidden bg-sahi-disable flex items-center justify-center max-h-[500px] ${className}`}
        onClick={(e) => onImageClick && onImageClick(0, e)}
      >
        <img
          src={validPhotos[0]}
          alt={title || "Post media"}
          className="w-full h-full max-h-[500px] object-cover transition-transform duration-300 hover:scale-[1.01]"
          onError={() => handleImgError(0)}
        />
      </div>
    );
  }

  if (count === 2) {
    return (
      <div className={`grid grid-cols-2 gap-1 w-full bg-sahi-disable max-h-[400px] overflow-hidden ${className}`}>
        {validPhotos.slice(0, 2).map((src, idx) => (
          <div
            key={idx}
            className="relative h-64 sm:h-72 cursor-pointer overflow-hidden bg-zinc-100"
            onClick={(e) => onImageClick && onImageClick(idx, e)}
          >
            <img
              src={src}
              alt={`${title || "Media"} ${idx + 1}`}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              onError={() => handleImgError(idx)}
            />
          </div>
        ))}
      </div>
    );
  }

  if (count === 3) {
    return (
      <div className={`grid grid-cols-2 gap-1 w-full bg-sahi-disable max-h-[420px] overflow-hidden ${className}`}>
        <div
          className="relative h-64 sm:h-80 cursor-pointer overflow-hidden bg-zinc-100"
          onClick={(e) => onImageClick && onImageClick(0, e)}
        >
          <img
            src={validPhotos[0]}
            alt={`${title || "Media"} 1`}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            onError={() => handleImgError(0)}
          />
        </div>
        <div className="grid grid-rows-2 gap-1 h-64 sm:h-80">
          {validPhotos.slice(1, 3).map((src, idx) => (
            <div
              key={idx + 1}
              className="relative h-full cursor-pointer overflow-hidden bg-zinc-100"
              onClick={(e) => onImageClick && onImageClick(idx + 1, e)}
            >
              <img
                src={src}
                alt={`${title || "Media"} ${idx + 2}`}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                onError={() => handleImgError(idx + 1)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 4 or more photos (up to 10) -> 2x2 grid with overlay on 4th image
  const displayPhotos = validPhotos.slice(0, 4);
  const extraCount = count - 4;

  return (
    <div className={`grid grid-cols-2 gap-1 w-full bg-sahi-disable max-h-[440px] overflow-hidden ${className}`}>
      {displayPhotos.map((src, idx) => {
        const isLast = idx === 3 && extraCount > 0;
        return (
          <div
            key={idx}
            className="relative h-36 sm:h-44 cursor-pointer overflow-hidden bg-zinc-100 group"
            onClick={(e) => onImageClick && onImageClick(idx, e)}
          >
            <img
              src={src}
              alt={`${title || "Media"} ${idx + 1}`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              onError={() => handleImgError(idx)}
            />
            {isLast && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-lg sm:text-xl transition-colors hover:bg-black/70">
                +{extraCount + 1}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Post Carousel (Instagram-style smooth scroll carousel with 3/4 badge) ───
export function PostCarousel({ photos = [], title = "", initialIndex = 0, className = "" }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  const containerRef = useRef(null);
  const [failedImages, setFailedImages] = useState({});
  const [dimensions, setDimensions] = useState({}); // { [idx]: { width, height, ratio } }

  const validPhotos = (photos || []).filter((_, idx) => !failedImages[idx]);
  const count = validPhotos.length;

  const handleImageLoad = (idx, e) => {
    const img = e.target;
    if (img && img.naturalWidth && img.naturalHeight) {
      setDimensions((prev) => ({
        ...prev,
        [idx]: {
          width: img.naturalWidth,
          height: img.naturalHeight,
          ratio: img.naturalWidth / img.naturalHeight
        }
      }));
    }
  };

  // 1. Measure natural dimensions of all images to calculate reference ratio R
  useEffect(() => {
    photos.forEach((src, idx) => {
      if (!src) return;
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          setDimensions((prev) => ({
            ...prev,
            [idx]: {
              width: img.naturalWidth,
              height: img.naturalHeight,
              ratio: img.naturalWidth / img.naturalHeight
            }
          }));
        }
      };
      img.onerror = () => {
        setFailedImages((prev) => ({ ...prev, [idx]: true }));
      };
      img.src = src;
    });
  }, [photos]);

  // Compute reference ratio R: find the tallest image (minimum ratio width/height) so the tallest image is 100% filled vertically
  const referenceRatio = (() => {
    const validDims = Object.entries(dimensions)
      .filter(([idx]) => !failedImages[idx])
      .map(([_, dim]) => dim.ratio)
      .filter((r) => typeof r === "number" && !isNaN(r) && r > 0);

    if (!validDims.length) return null; // No aspect-ratio until loaded to avoid premature default

    // Pick the tallest image (lowest ratio w/h = largest height)
    let minRatio = Math.min(...validDims);

    return minRatio;
  })();

  // 2. Initial glide from 0 to initialIndex
  useEffect(() => {
    if (!containerRef.current || count <= 1) return;
    const container = containerRef.current;

    const targetIndex = Math.max(0, Math.min(initialIndex || 0, count - 1));

    if (targetIndex <= 0) {
      container.scrollLeft = 0;
      setCurrentIndex(0);
      return;
    }

    container.scrollLeft = 0;
    setCurrentIndex(0);

    let animationFrameId = null;
    let timeoutId = null;

    const startGlide = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      if (!width) {
        animationFrameId = requestAnimationFrame(startGlide);
        return;
      }

      const target = targetIndex * width;
      const start = containerRef.current.scrollLeft;
      const distance = target - start;
      if (Math.abs(distance) < 1) {
        setCurrentIndex(targetIndex);
        return;
      }

      const duration = 450;
      let startTime = null;

      containerRef.current.style.scrollSnapType = "none";

      const easeInOutCubic = (t) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const ease = easeInOutCubic(progress);

        if (containerRef.current) {
          containerRef.current.scrollLeft = start + distance * ease;
        }

        if (progress < 1) {
          animationFrameId = requestAnimationFrame(animate);
        } else {
          if (containerRef.current) {
            containerRef.current.scrollLeft = target;
            containerRef.current.style.scrollSnapType = "x mandatory";
          }
          setCurrentIndex(targetIndex);
        }
      };

      animationFrameId = requestAnimationFrame(animate);
    };

    timeoutId = setTimeout(startGlide, 180);

    return () => {
      clearTimeout(timeoutId);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (containerRef.current) {
        containerRef.current.style.scrollSnapType = "x mandatory";
      }
    };
  }, [initialIndex, count]);

  const handleScroll = (e) => {
    const container = e.currentTarget;
    if (!container || container.clientWidth === 0) return;
    const scrollLeft = container.scrollLeft;
    const width = container.clientWidth;
    const index = Math.round(scrollLeft / width);
    if (index !== currentIndex && index >= 0 && index < count) {
      setCurrentIndex(index);
    }
  };

  const currentIndexRef = useRef(currentIndex);
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const scrollToImage = (index) => {
    if (!containerRef.current) return;
    containerRef.current.scrollTo({
      left: index * containerRef.current.clientWidth,
      behavior: "smooth"
    });
  };

  const handlePrev = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (currentIndexRef.current > 0) {
      scrollToImage(currentIndexRef.current - 1);
    }
  };

  const handleNext = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (currentIndexRef.current < count - 1) {
      scrollToImage(currentIndexRef.current + 1);
    }
  };

  // Keyboard Navigation (ArrowLeft / ArrowRight)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept arrow keys if the user is typing in an input, textarea, or contentEditable element
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (activeTag === "input" || activeTag === "textarea" || document.activeElement?.isContentEditable) {
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [count]);

  // Swipe / Drag handling for mobile swipe and desktop mouse dragging
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isDragging = useRef(false);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 35;
    if (diff > threshold && currentIndexRef.current < count - 1) {
      scrollToImage(currentIndexRef.current + 1);
    } else if (diff < -threshold && currentIndexRef.current > 0) {
      scrollToImage(currentIndexRef.current - 1);
    } else {
      // Snap back cleanly to current slide
      scrollToImage(currentIndexRef.current);
    }
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    touchStartX.current = e.clientX;
    touchEndX.current = e.clientX;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    touchEndX.current = e.clientX;
  };

  const handleMouseUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 35;
    if (diff > threshold && currentIndexRef.current < count - 1) {
      scrollToImage(currentIndexRef.current + 1);
    } else if (diff < -threshold && currentIndexRef.current > 0) {
      scrollToImage(currentIndexRef.current - 1);
    } else {
      scrollToImage(currentIndexRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (isDragging.current) {
      handleMouseUp();
    }
  };

  const handleImgError = (idx) => {
    setFailedImages((prev) => ({ ...prev, [idx]: true }));
  };

  if (!count) return null;

  // 1 Image view
  if (count === 1) {
    return (
      <div className={`relative w-full border-b border-sahi-border-color overflow-hidden flex items-center justify-center bg-zinc-100 ${className}`}>
        <div
          className="w-full flex items-center justify-center overflow-hidden"
          style={{
            aspectRatio: `${referenceRatio}`,
            maxHeight: "min(65vh, 560px)"
          }}
        >
          <img
            src={validPhotos[0]}
            alt={title || "Post media"}
            className="w-full h-full object-contain object-center max-w-full max-h-full"
            onError={() => handleImgError(0)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`relative w-full border-b border-sahi-border-color overflow-hidden group select-none bg-zinc-100 ${className}`}>
      {/* Viewport dynamically sized to tallest image ratio */}
      <div
        className="w-full relative overflow-hidden flex items-center justify-center"
        style={{
          aspectRatio: `${referenceRatio}`,
          maxHeight: "min(65vh, 560px)"
        }}
      >
        {/* Horizontal Smooth Scroll Container */}
        <div
          ref={containerRef}
          onScroll={handleScroll}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-none cursor-grab active:cursor-grabbing"
          style={{
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            scrollSnapType: "x mandatory"
          }}
        >
          {validPhotos.map((src, idx) => (
            <div
              key={idx}
              className="w-full h-full shrink-0 snap-center relative flex items-center justify-center bg-zinc-100 overflow-hidden"
              style={{
                width: "100%",
                height: "100%",
                scrollSnapAlign: "center",
                scrollSnapStop: "always"
              }}
            >
              <img
                src={src}
                alt={`${title || "Media"} ${idx + 1}`}
                className="w-full h-full object-contain object-center max-w-full max-h-full pointer-events-none"
                onLoad={(e) => handleImageLoad(idx, e)}
                onError={() => handleImgError(idx)}
              />
            </div>
          ))}
        </div>

        {/* Instagram-style Page Counter Badge (e.g. 3/4) Overlaid Inside Top-Right */}
        <div className="absolute top-3.5 right-3.5 z-20 bg-black/60 backdrop-blur-xs text-white text-[11px] font-semibold px-2.5 py-1 rounded-full pointer-events-none shadow-sm tracking-wide">
          {currentIndex + 1}/{count}
        </div>

        {/* Left Navigation Arrow Overlaid on Left Side */}
        {currentIndex > 0 && (
          <button
            type="button"
            onClick={handlePrev}
            aria-label="Previous image"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-black/50 hover:bg-black/75 backdrop-blur-xs text-white flex items-center justify-center transition-all shadow-md cursor-pointer border-0 outline-none active:scale-95"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Right Navigation Arrow Overlaid on Right Side */}
        {currentIndex < count - 1 && (
          <button
            type="button"
            onClick={handleNext}
            aria-label="Next image"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-black/50 hover:bg-black/75 backdrop-blur-xs text-white flex items-center justify-center transition-all shadow-md cursor-pointer border-0 outline-none active:scale-95"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Bottom Dots Indicator Overlaid Inside Bottom Center */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 bg-black/35 backdrop-blur-xs px-2.5 py-1 rounded-full pointer-events-none shadow-xs">
          {validPhotos.map((_, idx) => (
            <div
              key={idx}
              className={`rounded-full transition-all duration-200 ${idx === currentIndex
                  ? "w-2 h-2 bg-white scale-110 shadow-xs"
                  : "w-1.5 h-1.5 bg-white/50"
                }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Post Card Skeleton (feed loading state) ─────────────────────────────────
export function PostCardSkeleton() {
  return (
    <article className="bg-sahi-white border border-sahi-border-color rounded-[7px] shadow-xs overflow-hidden mb-3 p-4 space-y-3">
      {/* Top: Avatar & Author */}
      <div className="flex items-center gap-3">
        <Skeleton className="w-[42px] h-[42px] rounded-[7px] shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton className="h-3.5 w-1/3" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-10" />
          </div>
        </div>
      </div>

      {/* Title & Body */}
      <div className="space-y-2 pt-1">
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>

      {/* Middle Image */}
      <div className="pt-2">
        <Skeleton className="w-full h-64 rounded-lg" />
      </div>

      {/* Bottom Actions */}
      <div className="pt-3 border-t border-sahi-border-color/60">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 flex-1 rounded" />
          <Skeleton className="h-8 flex-1 rounded" />
          <Skeleton className="h-8 flex-1 rounded" />
        </div>
      </div>
    </article>
  );
}

// ─── Post Detail Skeleton (drawer/modal loading state) ───────────────────────
export function PostDetailSkeleton() {
  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-sahi-white">
      <div className="flex items-center justify-between px-4 py-4 border-b border-sahi-border-color shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
          <div className="space-y-2">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-5 w-3/4" />
        <div className="space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
}
